import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18next';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';

import { getUgcMediaVoteAndRankingInfo } from 'api/ugc';
import { hasUtcDTStartInLocal } from 'utils/dateTime';

import useClientErrorHandler from 'client/hooks/useClientErrorHandler';
import { usePrimaryPromoSelector } from 'client/game_photoContest/hooks/useReduxSelector';

export default function UgcVoteInfo({ mediaLibraryUUID, componentStyle = {} }) {
    const errHandler = useClientErrorHandler();
    const { id: contestPromoId } = usePrimaryPromoSelector(s => s.promo);
    const { startDate } = usePrimaryPromoSelector(s => s.settingPromo);
    const { isTestMode } = useSelector(s => s.promoState);
    const isVoteStart = useMemo(() => {
        if (isTestMode) {
            return true;
        } else if (startDate) {
            return hasUtcDTStartInLocal(startDate);
        }
        return false;
    }, [startDate, isTestMode]);
    const [isFetching, setIsFetching] = useState(false);
    const [mediaRank, setMediaRank] = useState(0);
    const [mediaVotes, setMediaVotes] = useState(0);

    useEffect(() => {
        async function get() {
            setIsFetching(true);
            try {
                const {
                    payload: { rank, votes }
                } = await getUgcMediaVoteAndRankingInfo(contestPromoId, mediaLibraryUUID);
                setMediaRank(rank);
                setMediaVotes(votes);
            } catch (err) {
                errHandler({ err });
            } finally {
                setIsFetching(false);
            }
        }
        if (isVoteStart) {
            get();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVoteStart]);

    return (
        <div css={containerStyle}>
            {isVoteStart ? (
                <>
                    <Typography variant="subtitle2" css={rankStyle(componentStyle)}>
                        {isFetching ? (
                            <Skeleton animation="wave" css={css(`width: 50px;`)} />
                        ) : (
                            `${i18n.t('label.contest_ugc.vote.rank')}${mediaRank}`
                        )}
                    </Typography>
                    <Typography variant="subtitle2" css={voteCountStyle(componentStyle)}>
                        {isFetching ? (
                            <Skeleton animation="wave" css={css(`width: 50px;`)} />
                        ) : (
                            `${i18n.t('label.contest_ugc.vote.count')}${mediaVotes}`
                        )}
                    </Typography>
                </>
            ) : (
                <Typography variant="subtitle2" css={notStartHintStyle}>
                    {i18n.t('label.contest_ugc.vote.not_yet_start')}
                </Typography>
            )}
        </div>
    );
}

const containerStyle = css`
    display: flex;
    align-items: center;
`;

const rankStyle = ({ buttonTextColor, buttonBgColor }) =>
    css`
        color: ${buttonTextColor};
        background-color: ${buttonBgColor};
        margin-right: 8px;
        padding: 2px 6px;
        border-radius: 4px;
    `;

const voteCountStyle = ({ buttonBgColor }) =>
    css`
        color: ${buttonBgColor};
    `;

const notStartHintStyle = css`
    color: #757575;
`;
